/**
 * Session Lock Service
 * 
 * Handles lock acquisition, release, and heartbeat management for the session lock system.
 * Prevents multiple users from simultaneously editing the same resource.
 */

import axios, { AxiosInstance } from 'axios';
import { Platform } from 'react-native';
import { logger } from '../utils/logger';

/**
 * Lock timeout in minutes.
 * Default: 10 minutes (must match backend SessionLockService.DEFAULT_LOCK_TIMEOUT_MINUTES).
 * Can be overridden via DLO_LOCK_TIMEOUT_MINUTES environment variable.
 */
const DEFAULT_LOCK_TIMEOUT_MINUTES = 10;

function getLockTimeoutMinutes(): number {
  try {
    const timeoutMinutes = process.env.DLO_LOCK_TIMEOUT_MINUTES;
    const minutes = timeoutMinutes ? parseFloat(timeoutMinutes) : DEFAULT_LOCK_TIMEOUT_MINUTES;
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return DEFAULT_LOCK_TIMEOUT_MINUTES;
    }
    return minutes;
  } catch (error) {
    logger.error('[SessionLock] Error parsing DLO_LOCK_TIMEOUT_MINUTES, using default', DEFAULT_LOCK_TIMEOUT_MINUTES, 'minutes');
    return DEFAULT_LOCK_TIMEOUT_MINUTES;
  }
}

function getLockTimeoutMs(): number {
  const minutes = getLockTimeoutMinutes();
  const milliseconds = minutes * 60 * 1000;
  logger.log('[SessionLock] Lock timeout configured to', minutes, 'minutes =', milliseconds, 'milliseconds');
  return milliseconds;
}

/**
 * Normalize a server-supplied ISO timestamp to a UTC string.
 *
 * The backend may return values like "2026-05-08T09:21:39.19677" (no timezone
 * designator). Per ECMAScript, an ISO date-time string without a timezone is
 * parsed as LOCAL time, which silently shifts the lease by the user's UTC
 * offset and breaks all expiration math. Append 'Z' so it is always treated
 * as UTC, matching the server's intent.
 */
function normalizeExpiresAt(serverIso: string): string {
  if (!serverIso) return serverIso;
  // Already has timezone info ('Z' or +/-hh:mm at the end) → leave alone.
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(serverIso)) return serverIso;
  return `${serverIso}Z`;
}

export interface LockAcquireRequest {
  module: string;
  submodule: string;
  id?: string; // For single ID resources (observations, walks, etc.)
  locId?: string; // For DLO schedule (composite key)
  period?: number;
  week?: number;
  year?: number;
  sessionId: string;
}

export interface LockAcquireResponse {
  success: boolean;
  message: string;
  lockToken: string;
  expiresAt: string;
  extended: boolean;
  // Optional fields (may or may not be provided by backend)
  resourceId?: string;
  lockTimeout?: number; // milliseconds
  heartbeatInterval?: number; // milliseconds
}

export interface LockConflictResponse {
  success: false;
  message: string;
  isLocked: boolean;
  alreadyLocked: boolean;
  lockedBy: string;
  lockedByName: string;
  lockedAt: string;
  expiresAt: string;
}

export interface LockReleaseRequest {
  lockToken: string;
}

/**
 * Session Lock Service
 * Manages lock lifecycle: acquire, heartbeat, and release
 */
export class SessionLockService {
  private api: AxiosInstance;
  private lockTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private heartbeatIntervals: Map<string, NodeJS.Timeout> = new Map();
  private expirationWarningTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private lastWarningTime: Map<string, number> = new Map(); // Track last warning time to prevent duplicate warnings
  private activeLocks: Map<string, LockAcquireResponse> = new Map();
  private warningActiveTimeouts: Set<string> = new Set(); // Track which resources have active warnings to prevent heartbeat updates
  private sessionId: string;
  private onExpirationWarning?: (resourceId: string, lockData: LockAcquireResponse) => void;
  private onAcquisitionFailure?: (error: any) => void;
  private onTimeoutExpiration?: (resourceId: string) => void;
  private onLockReleased?: (resourceId: string) => void;
  private boundBeforeUnloadHandler: (() => void) | null = null;
  private cachedAuthToken: string | null = null;
  private authInterceptorId: number | null = null;
  // Activity tracking — periodic heartbeat is skipped when user has been idle
  // longer than HEARTBEAT_IDLE_THRESHOLD_MS. The lock then drifts toward TTL
  // and the "Still there?" warning modal pops as designed.
  private lastActivityAt: number = Date.now();
  private boundActivityHandler: (() => void) | null = null;
  private static readonly HEARTBEAT_IDLE_THRESHOLD_MS = 2 * 60 * 1000; // 2 min

  constructor(api: AxiosInstance, sessionId: string) {
    this.api = api;
    this.sessionId = sessionId;
    this.setupAuthTokenCaching();
    this.setupUnloadHandler();
    this.setupActivityTracking();
  }

  /**
   * Intercept every outgoing request on our axios instance to cache the
   * Authorization bearer token.  This gives us a synchronous, always-fresh
   * token we can use inside the `beforeunload` handler (where async calls
   * like acquireTokenSilent are impossible).
   */
  private setupAuthTokenCaching(): void {
    this.authInterceptorId = this.api.interceptors.request.use((config) => {
      const authHeader = config.headers?.Authorization || config.headers?.authorization;
      if (typeof authHeader === 'string' && authHeader.startsWith('Bearer ')) {
        this.cachedAuthToken = authHeader; // store full "Bearer <token>" value
      }
      return config;
    });
  }

  /**
   * Allow external code (e.g. apiClient interceptor) to push the latest
   * bearer token into the service so it is available at beforeunload time.
   */
  setAuthToken(bearerToken: string): void {
    this.cachedAuthToken = bearerToken.startsWith('Bearer ')
      ? bearerToken
      : `Bearer ${bearerToken}`;
  }

  /**
   * Set up beforeunload and visibilitychange handlers to release all locks
   * when the user closes or refreshes the browser tab.
   * Uses fetch with keepalive:true so requests survive page teardown.
   */
  private setupUnloadHandler(): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    this.boundBeforeUnloadHandler = () => {
      this.releaseAllLocksOnUnload();
    };

    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);
    logger.log('[SessionLock] Registered beforeunload handler for lock cleanup');
  }

  /**
   * Remove the beforeunload handler (called on service teardown)
   */
  private removeUnloadHandler(): void {
    if (this.boundBeforeUnloadHandler && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
      this.boundBeforeUnloadHandler = null;
      logger.log('[SessionLock] Removed beforeunload handler');
    }
  }

  /**
   * Track user activity (mouse/keyboard/touch). Used by the periodic heartbeat
   * to skip ticks when the user is idle so we don't keep the lock alive on
   * an unattended tab.
   */
  private setupActivityTracking(): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    this.boundActivityHandler = () => {
      this.lastActivityAt = Date.now();
    };
    const events: (keyof WindowEventMap)[] = [
      'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
    ];
    events.forEach(e => window.addEventListener(e, this.boundActivityHandler!, { passive: true }));
  }

  private removeActivityTracking(): void {
    if (!this.boundActivityHandler || typeof window === 'undefined') return;
    const events: (keyof WindowEventMap)[] = [
      'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
    ];
    events.forEach(e => window.removeEventListener(e, this.boundActivityHandler!));
    this.boundActivityHandler = null;
  }

  /** Public hook for non-web (RN) callers to mark activity. */
  markActivity(): void {
    this.lastActivityAt = Date.now();
  }

  private isIdleForHeartbeat(): boolean {
    return Date.now() - this.lastActivityAt > SessionLockService.HEARTBEAT_IDLE_THRESHOLD_MS;
  }

  /**
   * Synchronously fire lock release requests for all active locks.
   * Uses navigator.sendBeacon (preferred) or fetch with keepalive:true
   * so the requests complete even after the page starts unloading.
   */
  private releaseAllLocksOnUnload(): void {
    if (this.activeLocks.size === 0) {
      return;
    }

    const baseURL = this.api.defaults.baseURL || '';
    const releaseUrl = `${baseURL}/common/lock/release`;

    logger.log(`[SessionLock] beforeunload: releasing ${this.activeLocks.size} active lock(s)`);
    logger.log(`[SessionLock] beforeunload: cached auth token available: ${!!this.cachedAuthToken}`);

    for (const [resourceId, lockData] of this.activeLocks.entries()) {
      try {
        const body = JSON.stringify({ lockToken: lockData.lockToken });
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };

        if (this.cachedAuthToken) {
          headers['Authorization'] = this.cachedAuthToken;
        }

        // Use fetch with keepalive:true — this is the only reliable way to
        // fire a request with custom headers that survives page teardown.
        // navigator.sendBeacon cannot set Authorization headers.
        if (typeof fetch !== 'undefined') {
          fetch(releaseUrl, {
            method: 'POST',
            headers,
            body,
            keepalive: true,
          });
          logger.log(`[SessionLock] beforeunload: fired release for ${resourceId}`);
        } else {
          logger.error(`[SessionLock] beforeunload: fetch API not available for ${resourceId}`);
        }
      } catch (error) {
        logger.error(`[SessionLock] beforeunload: failed to fire release for ${resourceId}:`, error);
      }
    }

    // Clear all timers synchronously
    this.heartbeatIntervals.forEach(interval => clearInterval(interval));
    this.lockTimeouts.forEach(timeout => clearTimeout(timeout));
    this.expirationWarningTimeouts.forEach(timeout => clearTimeout(timeout));
    this.heartbeatIntervals.clear();
    this.lockTimeouts.clear();
    this.expirationWarningTimeouts.clear();
    this.warningActiveTimeouts.clear();
    this.lastWarningTime.clear();
    this.activeLocks.clear();
  }

  /**
   * Register callback for expiration warning
   */
  setExpirationWarningCallback(callback: (resourceId: string, lockData: LockAcquireResponse) => void): void {
    this.onExpirationWarning = callback;
  }

  /**
   * Register callback for acquisition failure
   */
  setAcquisitionFailureCallback(callback: (error: any) => void): void {
    this.onAcquisitionFailure = callback;
  }

  /**
   * Register callback for timeout expiration (when lock times out without user action)
   */
  setTimeoutExpirationCallback(callback: (resourceId: string) => void): void {
    this.onTimeoutExpiration = callback;
  }

  /**
   * Register callback for lock released (clears warning popup in UI)
   * Called whenever a lock is released so the UI can dismiss any visible warning modal
   */
  setLockReleasedCallback(callback: (resourceId: string) => void): void {
    this.onLockReleased = callback;
  }

  /**
   * Trigger timeout expiration callback for a specific resource
   * This is called when a lock expires and needs to notify listeners
   */
  triggerTimeoutExpirationCallback(resourceId: string): void {
    if (this.onTimeoutExpiration) {
      logger.log('[SessionLock] Triggering timeout expiration callback for:', resourceId);
      this.onTimeoutExpiration(resourceId);
    }
  }

  /**
   * Generate a unique session ID if not already set
   */
  private getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = `browser-tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.sessionId;
  }

  /**
   * Generate a resource ID from request parameters
   */
  private generateResourceId(request: LockAcquireRequest): string {
    if (request.locId && request.period !== undefined && request.week && request.year) {
      return `${request.module}-${request.submodule}-LOC-${request.locId}-P${request.period}-W${request.week}-Y${request.year}`;
    }
    if (request.id) {
      return `${request.module}-${request.submodule}-ID-${request.id}`;
    }
    return `${request.module}-${request.submodule}-${Date.now()}`;
  }

  /**
   * Generate a resource ID from lock parameters (public method for checking existing locks)
   * Used to check if a lock already exists before acquiring a new one
   */
  static generateResourceIdFromParams(params: {
    module: string;
    submodule: string;
    id?: string;
    locId?: string;
    period?: number;
    week?: number;
    year?: number;
  }): string {
    if (params.locId && params.period !== undefined && params.week && params.year) {
      return `${params.module}-${params.submodule}-LOC-${params.locId}-P${params.period}-W${params.week}-Y${params.year}`;
    }
    if (params.id) {
      return `${params.module}-${params.submodule}-ID-${params.id}`;
    }
    return '';
  }

  /**
   * Get existing lock by resource parameters
   * Returns the lock data with the LATEST refreshed token if lock exists
   * This is critical for DLO APIs to get the valid (refreshed) lock token
   */
  getExistingLockByParams(params: {
    module: string;
    submodule: string;
    id?: string;
    locId?: string;
    period?: number;
    week?: number;
    year?: number;
  }): { resourceId: string; lockData: LockAcquireResponse } | null {
    const resourceId = SessionLockService.generateResourceIdFromParams(params);
    if (!resourceId) {
      return null;
    }
    
    const lockData = this.activeLocks.get(resourceId);
    if (lockData) {
      logger.log('[SessionLock] Found existing lock for resource:', resourceId);
      logger.log('[SessionLock]   Current lockToken:', lockData.lockToken?.substring(0, 20) + '...');
      logger.log('[SessionLock]   expiresAt:', lockData.expiresAt);
      return { resourceId, lockData };
    }
    
    return null;
  }

  /**
   * Acquire a lock for the specified resource
   * @param request Lock acquisition request parameters
   * @returns Lock token and metadata if successful
   * @throws Error with 423 status if resource is already locked
   */
  async acquireLock(request: LockAcquireRequest): Promise<LockAcquireResponse> {
    try {
      const lockRequest = {
        ...request,
        sessionId: this.getSessionId(),
        lockTimeoutMinutes: getLockTimeoutMinutes(),
      };

      logger.log('[SessionLock] Acquiring lock for resource:', lockRequest);

      const response = await this.api.post('/common/lock/acquire', lockRequest);

      if (response.data.success) {
        let lockData = response.data as LockAcquireResponse;
        
        // Generate resourceId if not provided by backend
        if (!lockData.resourceId) {
          lockData.resourceId = this.generateResourceId(request);
        }

        // Heartbeat interval: 3 minutes (must be < lock TTL of 10 min so we get multiple heartbeats per lease)
        const heartbeatInterval = 180000; // 3 minutes in milliseconds
        
        // Get lock timeout from environment configuration
        const lockTimeout = getLockTimeoutMs();

        // Ensure resourceId is a string (it will be from above)
        const resourceId = lockData.resourceId as string;

        // Store the active lock with updated expiration time
        const lockWithExpiry = {
          ...lockData,
          expiresAt: new Date(Date.now() + lockTimeout).toISOString(),
          heartbeatInterval,
          lockTimeout,
        };
        this.activeLocks.set(resourceId, lockWithExpiry);

        // CRITICAL: Clear any existing timers FIRST (in case of re-acquisition)
        // This prevents multiple timeouts from being active simultaneously
        const existingTimeoutId = this.lockTimeouts.get(resourceId);
        if (existingTimeoutId) {
          logger.log('[SessionLock] Clearing existing auto-release timeout during re-acquisition');
          clearTimeout(existingTimeoutId);
          this.lockTimeouts.delete(resourceId);
        }

        // Set up heartbeat to keep the lock alive
        this.setupHeartbeat(resourceId, lockData.lockToken, heartbeatInterval, lockTimeout);

        // Set up expiration warning (30 seconds before timeout)
        this.setupExpirationWarning(resourceId, lockTimeout);

        // NOTE: Do NOT set up auto-release here
        // Auto-release happens when:
        // 1. Warning modal expires (LockExpirationWarningModal handles this)
        // 2. User navigates away (cleanup useEffect)
        // 3. User saves (API success releases lock)

        logger.log('[SessionLock] Lock acquired:', resourceId);
        return lockWithExpiry;
      }

      throw new Error('Unexpected lock acquisition response');
    } catch (error: any) {
      // Notify callback of acquisition failure
      if (this.onAcquisitionFailure) {
        this.onAcquisitionFailure(error);
      }

      // Handle lock conflict (423 Locked)
      if (error.response?.status === 423) {
        const conflictData = error.response.data as LockConflictResponse;
        logger.warn('[SessionLock] Lock conflict - resource is locked by another user:', conflictData);
        error.lockConflict = conflictData;
        throw error;
      }

      // Handle bad request (400)
      if (error.response?.status === 400) {
        logger.error('[SessionLock] Bad request - missing required parameters:', error.response.data);
      }

      logger.error('[SessionLock] Failed to acquire lock:', error);
      throw error;
    }
  }

  /**
   * Release a lock
   * @param resourceId Resource identifier
   * @param lockToken Lock token from acquisition
   */
  async releaseLock(resourceId: string, lockToken: string): Promise<void> {
    try {
      logger.log('[SessionLock] Releasing lock for resource:', resourceId);

      // Clear heartbeat interval
      const heartbeatId = this.heartbeatIntervals.get(resourceId);
      if (heartbeatId) {
        clearInterval(heartbeatId);
        this.heartbeatIntervals.delete(resourceId);
        logger.log('[SessionLock] Cleared heartbeat interval for:', resourceId);
      }

      // Clear timeout
      const timeoutId = this.lockTimeouts.get(resourceId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.lockTimeouts.delete(resourceId);
        logger.log('[SessionLock] Cleared lock timeout for:', resourceId);
      }

      // Clear expiration warning
      const warningId = this.expirationWarningTimeouts.get(resourceId);
      if (warningId) {
        clearTimeout(warningId);
        this.expirationWarningTimeouts.delete(resourceId);
        logger.log('[SessionLock] Cleared expiration warning timeout for:', resourceId);
      }

      // Clear warning active state
      if (this.warningActiveTimeouts.has(resourceId)) {
        this.warningActiveTimeouts.delete(resourceId);
        logger.log('[SessionLock] Cleared warning active state for:', resourceId);
      }

      // Clear lastWarningTime to fully reset warning state for this resource
      this.lastWarningTime.delete(resourceId);
      logger.log('[SessionLock] Cleared lastWarningTime for:', resourceId);

      // Make release request
      await this.api.post('/common/lock/release', { lockToken });

      // Remove from active locks
      this.activeLocks.delete(resourceId);

      // Notify UI to hide any visible warning popup
      if (this.onLockReleased) {
        logger.log('[SessionLock] Calling onLockReleased callback to dismiss warning popup for:', resourceId);
        this.onLockReleased(resourceId);
      }

      logger.log('[SessionLock] Lock released successfully:', resourceId);
    } catch (error) {
      logger.error('[SessionLock] Failed to release lock:', error);
      // Don't throw - ensure cleanup happens even if release fails
      this.activeLocks.delete(resourceId);
      // Still clear timeouts and states even if API call fails
      if (this.heartbeatIntervals.has(resourceId)) {
        clearInterval(this.heartbeatIntervals.get(resourceId)!);
        this.heartbeatIntervals.delete(resourceId);
      }
      if (this.lockTimeouts.has(resourceId)) {
        clearTimeout(this.lockTimeouts.get(resourceId)!);
        this.lockTimeouts.delete(resourceId);
      }
      if (this.expirationWarningTimeouts.has(resourceId)) {
        clearTimeout(this.expirationWarningTimeouts.get(resourceId)!);
        this.expirationWarningTimeouts.delete(resourceId);
      }
      if (this.warningActiveTimeouts.has(resourceId)) {
        this.warningActiveTimeouts.delete(resourceId);
      }
      // Clear lastWarningTime even on failure
      this.lastWarningTime.delete(resourceId);
      // Notify UI to hide any visible warning popup even on failure
      if (this.onLockReleased) {
        this.onLockReleased(resourceId);
      }
    }
  }

  /**
   * Set up periodic heartbeat to keep lock alive
   */
  private setupHeartbeat(
    resourceId: string,
    lockToken: string,
    heartbeatInterval: number,
    lockTimeout: number
  ): void {
    // Send heartbeat immediately to ensure lock is extended
    this.sendHeartbeat(lockToken);

    // Set up periodic heartbeat every 5 minutes
    // CRITICAL: Use the LATEST lockToken from activeLocks for each heartbeat
    // This ensures we always use the most recent token if backend refreshes it
    const interval = setInterval(async () => {
      // CRITICAL: Skip heartbeat entirely if warning is active
      // When the expiration warning is shown, the user will either:
      // 1. Click "Continue" to refresh the lock (extending it)
      // 2. Click "Go Back" to release the lock
      // We should NOT send heartbeats that might interfere with these actions
      if (this.warningActiveTimeouts.has(resourceId)) {
        logger.log('[SessionLock] ⚠️  Skipping heartbeat - warning is active for', resourceId);
        return;
      }

      // Skip heartbeat if the user has been idle. Lets the lock drift toward
      // TTL so the "Still there?" warning fires for unattended tabs.
      if (this.isIdleForHeartbeat()) {
        logger.log('[SessionLock] ⏸️  Skipping heartbeat - user idle for', resourceId);
        return;
      }

      // CRITICAL: Get the LATEST lockToken from activeLocks, not the closure variable
      // The token may have been refreshed by a previous heartbeat
      const currentLockData = this.activeLocks.get(resourceId);
      if (!currentLockData) {
        logger.warn('[SessionLock] ⚠️  No lock data found for heartbeat, lock may have been released');
        return;
      }
      const currentLockToken = currentLockData.lockToken;
      logger.log('[SessionLock] 🔄 Using CURRENT lockToken for heartbeat:', currentLockToken?.substring(0, 20) + '...');

      const response = await this.sendHeartbeat(currentLockToken);
      // Update expiresAt AND lockToken from server's heartbeat response to stay in sync
      if (response?.data) {
        const lockData = this.activeLocks.get(resourceId);
        if (lockData) {
          const oldExpiresAt = lockData.expiresAt;
          const oldLockToken = lockData.lockToken;
          
          // Update expiresAt if provided
          if (response.data.expiresAt) {
            const newExpiresAt = normalizeExpiresAt(response.data.expiresAt);
            lockData.expiresAt = newExpiresAt;
            logger.log('[SessionLock] Updated expiresAt from periodic heartbeat response');
            logger.log('[SessionLock]   Old expiresAt:', oldExpiresAt);
            logger.log('[SessionLock]   New expiresAt:', newExpiresAt);
          }
          
          // CRITICAL: Update lockToken if backend returns a new one
          // This ensures subsequent API calls use the latest valid token
          if (response.data.lockToken && response.data.lockToken !== oldLockToken) {
            lockData.lockToken = response.data.lockToken;
            logger.log('[SessionLock] 🔑 Updated lockToken from heartbeat response');
            logger.log('[SessionLock]   Old lockToken:', oldLockToken?.substring(0, 20) + '...');
            logger.log('[SessionLock]   New lockToken:', response.data.lockToken?.substring(0, 20) + '...');
          }
        }
      }
    }, heartbeatInterval);

    this.heartbeatIntervals.set(resourceId, interval);
  }

  /**
   * Send heartbeat to keep lock alive
   */
  private async sendHeartbeat(lockToken: string): Promise<any> {
    try {
      const response = await this.api.post('/common/lock/heartbeat', {
        lockToken,
        extendByMinutes: getLockTimeoutMinutes(),
      });
      logger.log('[SessionLock] Heartbeat sent successfully');
      logger.log('[SessionLock] Heartbeat response data:', JSON.stringify(response?.data || response));
      return response;
    } catch (error) {
      logger.warn('[SessionLock] Heartbeat failed:', error);
      // Don't throw - heartbeat failure shouldn't break the app
      return null;
    }
  }

  /**
   * Set up expiration warning timeout
   * Shows a modal 30 seconds before the lock expires
   * Uses lockTimeout duration to avoid heartbeat interference
   */
  private setupExpirationWarning(resourceId: string, lockTimeout: number): void {
    logger.log(`[SessionLock] ========== setupExpirationWarning called for ${resourceId} ==========`);
    logger.log(`[SessionLock] Lock timeout: ${lockTimeout}ms (${(lockTimeout / 1000).toFixed(1)}s)`);
    
    // Clear previous warning if it exists
    const previousWarning = this.expirationWarningTimeouts.get(resourceId);
    if (previousWarning) {
      logger.log(`[SessionLock] Clearing previous warning timeout`);
      clearTimeout(previousWarning);
    }

    // Get the lock data
    const lockData = this.activeLocks.get(resourceId);
    if (!lockData) {
      logger.warn('[SessionLock] ❌ Lock data not found for expiration warning:', resourceId);
      return;
    }

    // Calculate delay based on lockTimeout duration, NOT on expiresAt
    // This prevents heartbeat updates from pushing the warning time further out
    // Warning should fire at: lockTimeout - 30 seconds
    const delayMs = lockTimeout - (30 * 1000); // Fire 30 seconds before timeout
    
    logger.log('[SessionLock] DEBUG: Expiration warning timing:');
    logger.log('  - Lock timeout duration:', lockTimeout, 'ms', `= ${(lockTimeout / 1000).toFixed(1)}s`);
    logger.log('  - Warning delay:', delayMs, 'ms', `= ${(delayMs / 1000).toFixed(1)}s`);
    logger.log('  - Warning will fire after lock has been held for:', `${((lockTimeout - delayMs) / 1000).toFixed(1)}s`);

    // Guard: If delay is less than 1 second, something is wrong
    if (delayMs < 1000) {
      logger.warn(
        `[SessionLock] ❌ Delay too short (${delayMs}ms < 1000ms), skipping warning setup for ${resourceId}`
      );
      return;
    }

    logger.log(`[SessionLock] ✅ Setting up warning timeout to fire in ${delayMs}ms (${(delayMs / 1000).toFixed(1)}s)`);
    console.log('🟡🟡🟡 setupExpirationWarning: Setting timeout for:', delayMs, 'ms', 'will fire at:', new Date(Date.now() + delayMs).toISOString());

    // Set warning to trigger 30 seconds before expiration
    const warningTimeout = setTimeout(() => {
      logger.log(`[SessionLock] ⏰ ⏰ ⏰ Warning timeout FIRED for ${resourceId} ⏰ ⏰ ⏰`);
      
      // GUARD: Check if lock is still active - if released, the timeout should have been cleared
      // This catches cases where timeout was about to fire but release happened just before
      const currentLockData = this.activeLocks.get(resourceId);
      if (!currentLockData) {
        logger.warn(`[SessionLock] ❌ Lock no longer exists for ${resourceId} - lock was released, ignoring warning`);
        return;
      }
      
      // Mark warning as ACTIVE to prevent heartbeat updates to expiresAt
      this.warningActiveTimeouts.add(resourceId);
      logger.log(`[SessionLock] Marked warning ACTIVE for ${resourceId}`);
      
      const currentTime = Date.now();
      const expiresAtTime = new Date(currentLockData.expiresAt).getTime();
      const timeUntilExpiration = expiresAtTime - currentTime;
      
      logger.log(`[SessionLock] Current state when warning fired:
        - Current time: ${new Date(currentTime).toISOString()}
        - Expires at: ${currentLockData.expiresAt}
        - Time until expiration: ${timeUntilExpiration}ms (${(timeUntilExpiration/1000).toFixed(1)}s)
        - Should be ~30 seconds`);
      
      if (this.onExpirationWarning) {
        // Guard: Don't show warning more than once per 5 seconds to prevent duplicate modals
        const lastWarning = this.lastWarningTime.get(resourceId) || 0;
        const timeSinceLastWarning = Date.now() - lastWarning;
        
        if (timeSinceLastWarning > 5000) {
          logger.log('[SessionLock] ✅ ✅ ✅ Calling onExpirationWarning callback to show modal');
          console.log('🔴🔴🔴 WARNING POPUP CALLBACK ABOUT TO FIRE 🔴🔴🔴');
          this.lastWarningTime.set(resourceId, Date.now());
          this.onExpirationWarning(resourceId, currentLockData);
          console.log('🔴🔴🔴 WARNING POPUP CALLBACK EXECUTED 🔴🔴🔴');
        } else {
          logger.warn(`[SessionLock] ❌ Ignoring duplicate warning (${timeSinceLastWarning}ms since last warning)`);
        }
      } else {
        logger.warn(`[SessionLock] ❌ Cannot show warning - callback not set`);
        console.log('🔴🔴🔴 WARNING POPUP FAILED - callback not set');
      }
    }, delayMs);

    this.expirationWarningTimeouts.set(resourceId, warningTimeout);
    logger.log(`[SessionLock] ✅ Warning timeout stored successfully. Will fire at ${new Date(Date.now() + delayMs).toISOString()}`);
  }

  /**
   * Refresh lock expiration (user clicks "Continue" on warning modal)
   * Extends the lock for another configured timeout duration
   */
  async refreshLockExpiration(resourceId: string, lockToken: string): Promise<void> {
    try {
      const refreshStartTime = new Date();
      logger.log('[SessionLock] ========================================');
      logger.log('[SessionLock] 👤 USER CLICKED CONTINUE IN WARNING MODAL');
      logger.log('[SessionLock] 🔄 REFRESH LOCK EXPIRATION STARTED');
      logger.log('[SessionLock] Current time:', refreshStartTime.toISOString(), `(${refreshStartTime.getTime()})`);
      logger.log('[SessionLock] Resource ID:', resourceId);
      logger.log('[SessionLock] ========================================');
      
      // Get lock timeout from environment configuration
      const lockTimeout = getLockTimeoutMs();
      const timeoutMinutes = lockTimeout / (60 * 1000);
      logger.log('[SessionLock] Lock timeout:', lockTimeout, 'ms', `(${timeoutMinutes} minutes)`);
      
      // CRITICAL: Clear ALL old timers FIRST to prevent race conditions
      // Must clear auto-release timeout IMMEDIATELY to prevent accidental lock release during refresh
      logger.log('[SessionLock] ========== CLEARING OLD TIMERS ==========');
      const oldTimeoutId = this.lockTimeouts.get(resourceId);
      if (oldTimeoutId) {
        logger.log('[SessionLock] 🛑 PRIORITY: Clearing old auto-release timeout (ID:', oldTimeoutId, ')');
        clearTimeout(oldTimeoutId);
        this.lockTimeouts.delete(resourceId);
        logger.log('[SessionLock] ✅ Old auto-release timeout cleared');
      } else {
        logger.log('[SessionLock] ℹ️  No old auto-release timeout found');
      }

      // Clear old expiration warning timeout
      const oldWarningId = this.expirationWarningTimeouts.get(resourceId);
      if (oldWarningId) {
        logger.log('[SessionLock] Clearing old warning timeout (ID:', oldWarningId, ')');
        clearTimeout(oldWarningId);
        this.expirationWarningTimeouts.delete(resourceId);
        logger.log('[SessionLock] ✅ Old warning timeout cleared');
      } else {
        logger.log('[SessionLock] ℹ️  No old warning timeout found');
      }

      // Clear old heartbeat interval
      const oldHeartbeatId = this.heartbeatIntervals.get(resourceId);
      if (oldHeartbeatId) {
        logger.log('[SessionLock] Clearing old heartbeat interval (ID:', oldHeartbeatId, ')');
        clearInterval(oldHeartbeatId);
        this.heartbeatIntervals.delete(resourceId);
        logger.log('[SessionLock] ✅ Old heartbeat interval cleared');
      } else {
        logger.log('[SessionLock] ℹ️  No old heartbeat interval found');
      }
      
      // Now that all old timers are cleared, send heartbeat and wait for response
      logger.log('[SessionLock] 🔄 Sending heartbeat and waiting for response');
      const heartbeatResponse = await this.sendHeartbeat(lockToken);
      let serverExpiresAt: string;
      let refreshedLockToken = lockToken; // Track if server returns a new token
      
      if (heartbeatResponse?.data?.expiresAt) {
        // Use the server's expiration time from heartbeat response
        serverExpiresAt = normalizeExpiresAt(heartbeatResponse.data.expiresAt);
        logger.log('[SessionLock] ✅ Using server expiration time from heartbeat response');
        logger.log('[SessionLock]   Server expiresAt:', serverExpiresAt);
      } else {
        // Fallback: calculate locally if heartbeat fails
        serverExpiresAt = new Date(Date.now() + lockTimeout).toISOString();
        logger.log('[SessionLock] ⚠️  Heartbeat response missing expiresAt, using calculated time');
        logger.log('[SessionLock]   Calculated expiresAt:', serverExpiresAt);
      }
      
      // CRITICAL: Check if server returned a refreshed lockToken
      if (heartbeatResponse?.data?.lockToken && heartbeatResponse.data.lockToken !== lockToken) {
        refreshedLockToken = heartbeatResponse.data.lockToken;
        logger.log('[SessionLock] ✅ Server returned REFRESHED lockToken');
        logger.log('[SessionLock]   Old token:', lockToken?.substring(0, 20) + '...');
        logger.log('[SessionLock]   New token:', refreshedLockToken?.substring(0, 20) + '...');
      }

      // IMPORTANT: Clear lastWarningTime to allow new warning to show
      logger.log('[SessionLock] Clearing lastWarningTime to allow new warnings');
      this.lastWarningTime.delete(resourceId);

      // Update lock expiration time with server's time from heartbeat response
      logger.log('[SessionLock] ========== UPDATING LOCK EXPIRATION DATA ==========');
      const lockData = this.activeLocks.get(resourceId);
      if (lockData) {
        const oldExpiresAt = lockData.expiresAt;
        const oldExpiresTime = new Date(oldExpiresAt).getTime();
        const newExpiresTime = new Date(serverExpiresAt).getTime();
        const actualTimeUntilExpiration = newExpiresTime - Date.now();
        
        logger.log('[SessionLock] Updating expiresAt from heartbeat response');
        logger.log('[SessionLock] Time calculations:');
        logger.log('  - Current time:', Date.now(), `(${new Date().toISOString()})`);
        logger.log('  - Old expiresAt:', oldExpiresTime, `(${oldExpiresAt})`);
        logger.log('  - New expiresAt:', newExpiresTime, `(${serverExpiresAt})`);
        logger.log('  - Time until expiry:', Math.round(actualTimeUntilExpiration / 1000), 'seconds');
        
        // IMPORTANT: Create a new object instead of mutating (object may be immutable from Redux)
        // CRITICAL: Also update lockToken if server returned a refreshed token
        const updatedLockData = {
          ...lockData,
          expiresAt: serverExpiresAt,
          lockToken: refreshedLockToken, // Use the refreshed token (or original if not refreshed)
        };
        this.activeLocks.set(resourceId, updatedLockData);
        
        // Verify the update took effect
        const verifyLockData = this.activeLocks.get(resourceId);
        logger.log('[SessionLock] ✅ Lock expiration data updated in activeLocks Map');
        logger.log('[SessionLock] Verification:');
        logger.log('  - Stored expiresAt:', verifyLockData?.expiresAt);
        logger.log('  - Stored lockToken:', verifyLockData?.lockToken?.substring(0, 20) + '...');
      } else {
        logger.error('[SessionLock] ❌ Cannot refresh - lock data not found for:', resourceId);
        return;
      }

      // Now set up periodic heartbeat (for future intervals, not the immediate one we just sent)
      // Also update expiresAt AND lockToken from heartbeat response to keep it in sync with server
      logger.log('[SessionLock] 🔄 Setting up periodic heartbeat interval');
      const interval = setInterval(async () => {
        // CRITICAL: Skip heartbeat entirely if warning is active
        // When the expiration warning is shown, the user will either:
        // 1. Click "Continue" to refresh the lock (extending it)
        // 2. Click "Go Back" to release the lock
        // We should NOT send heartbeats that might interfere with these actions
        if (this.warningActiveTimeouts.has(resourceId)) {
          logger.log('[SessionLock] ⚠️  Skipping heartbeat - warning is active for', resourceId);
          return;
        }

        // Skip heartbeat if the user has been idle.
        if (this.isIdleForHeartbeat()) {
          logger.log('[SessionLock] ⏸️  Skipping heartbeat - user idle for', resourceId);
          return;
        }

        // CRITICAL: Get the LATEST lockToken from activeLocks, not the closure variable
        // The token may have been refreshed by a previous heartbeat
        const currentLockData = this.activeLocks.get(resourceId);
        if (!currentLockData) {
          logger.warn('[SessionLock] ⚠️  No lock data found for heartbeat, lock may have been released');
          return;
        }
        const currentLockToken = currentLockData.lockToken;
        logger.log('[SessionLock] 🔄 Using CURRENT lockToken for heartbeat:', currentLockToken?.substring(0, 20) + '...');

        const response = await this.sendHeartbeat(currentLockToken);
        // Update expiresAt AND lockToken from server's heartbeat response to stay in sync
        if (response?.data) {
          const lockData = this.activeLocks.get(resourceId);
          if (lockData) {
            const oldExpiresAt = lockData.expiresAt;
            const oldLockToken = lockData.lockToken;
            
            // Update expiresAt if provided
            if (response.data.expiresAt) {
              const newExpiresAt = normalizeExpiresAt(response.data.expiresAt);
              lockData.expiresAt = newExpiresAt;
              logger.log('[SessionLock] Updated expiresAt from periodic heartbeat response');
              logger.log('[SessionLock]   Old expiresAt:', oldExpiresAt);
              logger.log('[SessionLock]   New expiresAt:', newExpiresAt);
            }
            
            // CRITICAL: Update lockToken if backend returns a new one
            // This ensures subsequent API calls use the latest valid token
            if (response.data.lockToken && response.data.lockToken !== oldLockToken) {
              lockData.lockToken = response.data.lockToken;
              logger.log('[SessionLock] 🔑 Updated lockToken from heartbeat response');
              logger.log('[SessionLock]   Old lockToken:', oldLockToken?.substring(0, 20) + '...');
              logger.log('[SessionLock]   New lockToken:', response.data.lockToken?.substring(0, 20) + '...');
            }
          }
        }
      }, 180000); // 3 minutes
      this.heartbeatIntervals.set(resourceId, interval);

      // Now set up new expiration warning (30 seconds before new timeout)
      // Use the actual time from server's expiresAt, not the lockTimeout
      logger.log(`[SessionLock] ========== Setting up NEW TIMEOUTS after refresh ==========`);
      logger.log('[SessionLock] DEBUG: Before calling setupExpirationWarning:');
      const preWarningLockData = this.activeLocks.get(resourceId);
      logger.log('  - lockData exists:', !!preWarningLockData);
      logger.log('  - lockData.expiresAt:', preWarningLockData?.expiresAt);
      logger.log('  - lockData.expiresAt timestamp:', new Date(preWarningLockData?.expiresAt || '').getTime());
      logger.log('  - Time until expiresAt:', new Date(preWarningLockData?.expiresAt || '').getTime() - Date.now(), 'ms');
      this.setupExpirationWarning(resourceId, lockTimeout);
      
      // Set up new auto-release timeout based on actual time until expiration
      // This ensures auto-release fires exactly when server says lock expires
      const actualExpiresTime = new Date(serverExpiresAt).getTime();
      const actualTimeUntilAutoRelease = Math.max(0, actualExpiresTime - Date.now());
      logger.log(`[SessionLock] Setting up auto-release with actual time from server`);
      logger.log(`[SessionLock]   Actual time until auto-release: ${actualTimeUntilAutoRelease}ms (${Math.round(actualTimeUntilAutoRelease/1000)}s)`);
      this.setupAutoReleaseWithDelay(resourceId, actualTimeUntilAutoRelease);

      // Verify the warning was set up
      const hasWarning = this.expirationWarningTimeouts.has(resourceId);
      const hasAutoRelease = this.lockTimeouts.has(resourceId);
      logger.log(`[SessionLock] ========== REFRESH COMPLETE ==========`);
      logger.log(`[SessionLock] ✅ Lock extended for another ${timeoutMinutes} minute(s)`);
      logger.log(`[SessionLock] ✅ Warning timeout active: ${hasWarning}`);
      logger.log(`[SessionLock] ✅ Auto-release timeout active: ${hasAutoRelease}`);
    } catch (error) {
      logger.error('[SessionLock] Error refreshing lock expiration:', error);
    }
  }

  /**
   * Set up auto-release timeout
   * If heartbeat fails consistently, auto-release the lock to prevent stale locks
   */
  private setupAutoRelease(resourceId: string, lockTimeout: number): void {
    const scheduledReleaseTime = new Date(Date.now() + lockTimeout).toISOString();
    logger.log(`[SessionLock] ========== Setting up AUTO-RELEASE TIMEOUT ==========`);
    logger.log(`[SessionLock] Resource ID: ${resourceId}`);
    logger.log(`[SessionLock] Timeout: ${lockTimeout}ms (${lockTimeout/1000}s)`);
    logger.log(`[SessionLock] Current time: ${new Date().toISOString()} (${Date.now()})`);
    logger.log(`[SessionLock] Will fire at: ${scheduledReleaseTime}`);
    
    const timeout = setTimeout(async () => {
      const fireTime = new Date();
      logger.warn('[SessionLock] ========================================');
      logger.warn('[SessionLock] ⏰ AUTO-RELEASE TIMEOUT FIRED');
      logger.warn('[SessionLock] ========================================');
      logger.warn('[SessionLock] Fire time:', fireTime.toISOString(), `(${fireTime.getTime()})`);
      logger.warn('[SessionLock] Resource ID:', resourceId);
      logger.warn('[SessionLock] Releasing lock and triggering navigation callback');
      
      // CRITICAL: Clear heartbeat interval FIRST to stop sending heartbeats
      const heartbeatId = this.heartbeatIntervals.get(resourceId);
      if (heartbeatId) {
        logger.log('[SessionLock] Clearing heartbeat interval before release');
        clearInterval(heartbeatId);
        this.heartbeatIntervals.delete(resourceId);
      }

      // Clear expiration warning
      const warningId = this.expirationWarningTimeouts.get(resourceId);
      if (warningId) {
        clearTimeout(warningId);
        this.expirationWarningTimeouts.delete(resourceId);
      }
      
      const lockData = this.activeLocks.get(resourceId);
      if (lockData) {
        try {
          // Actually release the lock via API
          await this.api.post('/common/lock/release', { lockToken: lockData.lockToken });
          logger.log('[SessionLock] ✅ Lock released via API');
        } catch (error) {
          logger.error('[SessionLock] ❌ Failed to release lock via API:', error);
        }
      }
      
      // Clean up internal state
      this.activeLocks.delete(resourceId);
      this.lockTimeouts.delete(resourceId);
      
      // Trigger timeout expiration callback for navigation
      if (this.onTimeoutExpiration) {
        logger.log('[SessionLock] 🚀 Triggering navigation callback');
        this.onTimeoutExpiration(resourceId);
      }
    }, lockTimeout);

    this.lockTimeouts.set(resourceId, timeout);
    logger.log(`[SessionLock] ✅ Auto-release timeout stored for ${resourceId}`);
  }

  /**
   * Set up auto-release with specific delay (used after refresh with server's actual time)
   * Similar to setupAutoRelease but accepts the actual delay instead of lockTimeout
   */
  private setupAutoReleaseWithDelay(resourceId: string, delayMs: number): void {
    const scheduledReleaseTime = new Date(Date.now() + delayMs).toISOString();
    logger.log(`[SessionLock] ========== Setting up AUTO-RELEASE WITH ACTUAL DELAY ==========`);
    logger.log(`[SessionLock] Resource ID: ${resourceId}`);
    logger.log(`[SessionLock] Delay: ${delayMs}ms (${Math.round(delayMs/1000)}s)`);
    logger.log(`[SessionLock] Current time: ${new Date().toISOString()} (${Date.now()})`);
    logger.log(`[SessionLock] Will fire at: ${scheduledReleaseTime}`);
    
    const timeout = setTimeout(async () => {
      const fireTime = new Date();
      logger.warn('[SessionLock] ========================================');
      logger.warn('[SessionLock] ⏰ AUTO-RELEASE TIMEOUT FIRED');
      logger.warn('[SessionLock] ========================================');
      logger.warn('[SessionLock] Fire time:', fireTime.toISOString(), `(${fireTime.getTime()})`);
      logger.warn('[SessionLock] Resource ID:', resourceId);
      logger.warn('[SessionLock] Releasing lock and triggering navigation callback');
      
      // CRITICAL: Clear heartbeat interval FIRST to stop sending heartbeats
      const heartbeatId = this.heartbeatIntervals.get(resourceId);
      if (heartbeatId) {
        logger.log('[SessionLock] Clearing heartbeat interval before release');
        clearInterval(heartbeatId);
        this.heartbeatIntervals.delete(resourceId);
      }

      // Clear expiration warning
      const warningId = this.expirationWarningTimeouts.get(resourceId);
      if (warningId) {
        clearTimeout(warningId);
        this.expirationWarningTimeouts.delete(resourceId);
      }
      
      const lockData = this.activeLocks.get(resourceId);
      if (lockData) {
        try {
          // Actually release the lock via API
          await this.api.post('/common/lock/release', { lockToken: lockData.lockToken });
          logger.log('[SessionLock] ✅ Lock released via API');
        } catch (error) {
          logger.error('[SessionLock] ❌ Failed to release lock via API:', error);
        }
      }
      
      // Clean up internal state
      this.activeLocks.delete(resourceId);
      this.lockTimeouts.delete(resourceId);
      
      // Trigger timeout expiration callback for navigation
      if (this.onTimeoutExpiration) {
        logger.log('[SessionLock] 🚀 Triggering navigation callback');
        this.onTimeoutExpiration(resourceId);
      }
    }, delayMs);

    this.lockTimeouts.set(resourceId, timeout);
    logger.log(`[SessionLock] ✅ Auto-release timeout stored for ${resourceId}`);
  }

  /**
   * Mark warning as inactive (called when modal is dismissed or Continue is clicked)
   * Allows heartbeat to resume updating expiresAt
   */
  clearWarningActiveFlag(resourceId: string): void {
    if (this.warningActiveTimeouts.has(resourceId)) {
      logger.log('[SessionLock] ✅ Clearing warning ACTIVE flag for', resourceId);
      this.warningActiveTimeouts.delete(resourceId);
    }
  }

  /**
   * Get active lock for a resource
   */
  getActiveLock(resourceId: string): LockAcquireResponse | undefined {
    return this.activeLocks.get(resourceId);
  }

  /**
   * Check if a resource is locked
   */
  isResourceLocked(resourceId: string): boolean {
    return this.activeLocks.has(resourceId);
  }

  /**
   * Get all active locks
   */
  getActiveLocks(): Map<string, LockAcquireResponse> {
    return new Map(this.activeLocks);
  }

  /**
   * Public cleanup method - removes event listeners and interceptors.
   * Call this before discarding the service instance.
   */
  cleanup(): void {
    this.removeUnloadHandler();
    this.removeActivityTracking();
    if (this.authInterceptorId !== null) {
      this.api.interceptors.request.eject(this.authInterceptorId);
      this.authInterceptorId = null;
    }
  }

  /**
   * Release all locks (cleanup on logout or navigation)
   */
  async releaseAllLocks(): Promise<void> {
    logger.log('[SessionLock] Releasing all locks');
    
    for (const [resourceId, lockData] of this.activeLocks.entries()) {
      try {
        await this.releaseLock(resourceId, lockData.lockToken);
      } catch (error) {
        logger.warn(`[SessionLock] Failed to release lock for ${resourceId}:`, error);
      }
    }

    // Clear all timeouts and intervals
    this.lockTimeouts.forEach(timeout => clearTimeout(timeout));
    this.heartbeatIntervals.forEach(interval => clearInterval(interval));
    this.expirationWarningTimeouts.forEach(timeout => clearTimeout(timeout));
    
    this.lockTimeouts.clear();
    this.heartbeatIntervals.clear();
    this.expirationWarningTimeouts.clear();
    this.warningActiveTimeouts.clear();
    this.lastWarningTime.clear();
    this.activeLocks.clear();
  }
}

/**
 * Create a singleton instance of SessionLockService
 */
let sessionLockServiceInstance: SessionLockService | null = null;

export const initializeSessionLockService = (api: AxiosInstance, sessionId?: string): SessionLockService => {
  if (!sessionLockServiceInstance) {
    sessionLockServiceInstance = new SessionLockService(api, sessionId || '');
  }
  return sessionLockServiceInstance;
};

export const getSessionLockService = (): SessionLockService | null => {
  return sessionLockServiceInstance;
};

export const resetSessionLockService = (): void => {
  if (sessionLockServiceInstance) {
    sessionLockServiceInstance.cleanup();
    sessionLockServiceInstance.releaseAllLocks().catch(err => {
      logger.error('[SessionLock] Error during reset:', err);
    });
  }
  sessionLockServiceInstance = null;
};
